import GridCard from '@Guidelines/Component/GridCard';
import { theme } from '@Guidelines/Theme/theme';
import ContactCardActions from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/ContactCard/ContactCardActions';
import type BeneficiaryContactView from '@UI/Store/BeneficiaryContacts/Views/BeneficiaryContactView';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Tag, type TagStyle } from '@verclam/ui-kit';

const NOT_PROVIDED_LABEL = 'Non renseigné';
const OTHER_ROLE_VALUE = 'other';
const CONTACT_CARD_TRACK_COUNT = 5;

const nameRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.625rem;
	flex-wrap: wrap;
`;

const nameStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const fieldStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
`;

const fieldLabelStyle = css`
	${theme.typography.eyebrow}
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const fieldValueStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

/**
 * Groups the two optional fields — contact details and notes — into a single subgrid track, so a
 * contact missing both still emits one empty track instead of two.
 */
const optionalFieldsStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.875rem;
`;

// Same bg/text pairing as the kit's own "help" tokens (`uiKitTokens.ts`): the teal surface/ink
// combination is the project's established readable pairing, not a one-off color choice.
const trustTagStyle: TagStyle = {
	'--ui-kit-tag-bg': theme.tints.teal.surface,
	'--ui-kit-tag-text': theme.tints.teal.ink,
};

type Props = {
	readonly contact: BeneficiaryContactView;
};

/**
 * One contact, laid out as a five-track subgrid cell: name row, type, parentage, an optional block
 * grouping contact details and notes, and actions. The role's precision only ever qualifies "other":
 * any other role, or none, shows the role alone.
 */
const ContactCard: React.FC<Props> = ({ contact }) => {
	const roleText =
		contact.role !== null
			? contact.role.value === OTHER_ROLE_VALUE && contact.roleDetails !== null
				? `${contact.role.label} — ${contact.roleDetails}`
				: contact.role.label
			: NOT_PROVIDED_LABEL;

	return (
		<GridCard trackCount={CONTACT_CARD_TRACK_COUNT} rowGap="0.875rem">
			<div css={nameRowStyle}>
				<h2 css={nameStyle}>{contact.fullName}</h2>
				{contact.isPersonOfTrust && <Tag value="Personne de confiance" style={trustTagStyle} />}
			</div>
			<div css={fieldStyle}>
				<span css={fieldLabelStyle}>Type</span>
				<p css={fieldValueStyle}>{roleText}</p>
			</div>
			<div css={fieldStyle}>
				<span css={fieldLabelStyle}>Lien de parenté</span>
				<p css={fieldValueStyle}>{contact.relationship.label}</p>
			</div>
			{/* Always rendered, even empty: this is the subgrid's fourth row track. Omitting it when
			neither field below has data would drop the track for this card only, shifting its actions
			out of alignment with sibling cards that do have details or notes to show. */}
			<div css={optionalFieldsStyle}>
				{(contact.phoneNumber !== null || contact.email !== null) && (
					<div css={fieldStyle}>
						<span css={fieldLabelStyle}>Coordonnées</span>
						{contact.phoneNumber !== null && <p css={fieldValueStyle}>{contact.phoneNumber}</p>}
						{contact.email !== null && <p css={fieldValueStyle}>{contact.email}</p>}
					</div>
				)}
				{contact.notes !== null && (
					<div css={fieldStyle}>
						<span css={fieldLabelStyle}>Notes</span>
						<p css={fieldValueStyle}>{contact.notes}</p>
					</div>
				)}
			</div>
			<ContactCardActions contact={contact} />
		</GridCard>
	);
};

export default ContactCard;
